import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import PushToggle from "../components/PushToggle";

const TYPE_META = {
  canguro: { label: "Canguro", icon: "fa-baby", color: "blue", subtitle: "Cuido niños" },
  profesor: { label: "Profesor/a", icon: "fa-graduation-cap", color: "teal", subtitle: "Doy clases" },
};

export default function MyProfile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [myProfiles, setMyProfiles] = useState([]);

  useEffect(() => {
    api.get("/profiles/mine/all").then(({ data }) => setMyProfiles(data || [])).catch(() => {});
  }, []);

  if (!user) return null;

  const initials = user.name?.substring(0, 1).toUpperCase() || "U";
  const hasCanguro = myProfiles.some((p) => p.type === "canguro");
  const hasProfesor = myProfiles.some((p) => p.type === "profesor");
  const missingType = !hasCanguro ? "canguro" : !hasProfesor ? "profesor" : null;

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const statusBadge = (status) => {
    const map = {
      aprobado: "bg-green-100 text-green-700",
      rechazado: "bg-red-100 text-red-700",
      pendiente: "bg-amber-100 text-amber-700",
    };
    return map[status] || "bg-amber-100 text-amber-700";
  };

  return (
    <div className="space-y-5 animate-fade-in-up max-w-2xl mx-auto">
      <h1 className="text-2xl md:text-3xl font-extrabold">Mi perfil</h1>

      <div className="kibli-card p-6 text-center">
        <div className="w-24 h-24 mx-auto rounded-full bg-blue-600 text-white flex items-center justify-center text-4xl font-extrabold" data-testid="profile-avatar">
          {initials}
        </div>
        <div className="mt-4 text-xl font-bold" data-testid="profile-name">{user.name}</div>
        <div className="text-slate-500" data-testid="profile-email">{user.email}</div>
        {user.verified && (
          <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-100 text-green-700 text-sm font-semibold">
            <i className="fa-solid fa-shield-halved"></i> Cuenta verificada
          </div>
        )}
      </div>

      {/* Profiles section */}
      <div className="kibli-card overflow-hidden">
        {myProfiles.length === 0 ? (
          <Link to="/profile/create" className="flex items-center gap-3 px-5 py-4 hover:bg-slate-50 transition" data-testid="create-prof-profile-link">
            <span className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center">
              <i className="fa-solid fa-plus"></i>
            </span>
            <span className="flex-1 font-semibold text-slate-900">Crear mi perfil profesional</span>
            <i className="fa-solid fa-chevron-right text-slate-400"></i>
          </Link>
        ) : (
          <>
            {myProfiles.map((p) => {
              const meta = TYPE_META[p.type] || TYPE_META.canguro;
              return (
                <React.Fragment key={p.id}>
                  <Link
                    to={`/profile/edit/${p.id}`}
                    className="flex items-center gap-3 px-5 py-4 hover:bg-slate-50 transition"
                    data-testid={`edit-profile-${p.type}-link`}
                  >
                    <span className={`w-10 h-10 rounded-full bg-${meta.color}-100 text-${meta.color}-600 flex items-center justify-center`}>
                      <i className={`fa-solid ${meta.icon}`}></i>
                    </span>
                    <span className="flex-1">
                      <span className="block font-semibold text-slate-900">Mi perfil de {meta.label.toLowerCase()}</span>
                      <span className="text-xs text-slate-500">{meta.subtitle}</span>
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full font-semibold ${statusBadge(p.status)}`} data-testid={`profile-status-${p.type}`}>
                      {p.status}
                    </span>
                    <i className="fa-solid fa-chevron-right text-slate-400 ml-1"></i>
                  </Link>
                  <div className="border-t border-slate-100"></div>
                </React.Fragment>
              );
            })}
            {missingType && (
              <Link
                to={`/profile/create?type=${missingType}`}
                className="flex items-center gap-3 px-5 py-4 hover:bg-slate-50 transition bg-blue-50/40"
                data-testid={`create-other-${missingType}-link`}
              >
                <span className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center">
                  <i className="fa-solid fa-plus"></i>
                </span>
                <span className="flex-1">
                  <span className="block font-semibold text-slate-900">
                    Crear también mi perfil de {TYPE_META[missingType].label.toLowerCase()}
                  </span>
                  <span className="text-xs text-slate-500">
                    Aparecerás también como {TYPE_META[missingType].label.toLowerCase()} para más oportunidades
                  </span>
                </span>
                <i className="fa-solid fa-chevron-right text-slate-400"></i>
              </Link>
            )}
          </>
        )}

        <div className="border-t border-slate-100"></div>
        <PushToggle />
        <div className="border-t border-slate-100"></div>
        <Link to="/favorites" className="flex items-center gap-3 px-5 py-4 hover:bg-slate-50 transition" data-testid="favorites-link">
          <span className="w-10 h-10 rounded-full bg-red-100 text-red-500 flex items-center justify-center">
            <i className="fa-regular fa-heart"></i>
          </span>
          <span className="flex-1 font-semibold text-slate-900">Mis favoritos</span>
          <i className="fa-solid fa-chevron-right text-slate-400"></i>
        </Link>
        <div className="border-t border-slate-100"></div>
        <a href="mailto:soporte@kibliapp.com" className="flex items-center gap-3 px-5 py-4 hover:bg-slate-50 transition" data-testid="help-link">
          <span className="w-10 h-10 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center">
            <i className="fa-regular fa-circle-question"></i>
          </span>
          <span className="flex-1 font-semibold text-slate-900">Ayuda</span>
          <span className="text-sm text-slate-500 hidden sm:inline">soporte@kibliapp.com</span>
          <i className="fa-solid fa-chevron-right text-slate-400"></i>
        </a>
      </div>

      <button onClick={handleLogout} data-testid="logout-btn"
        className="w-full py-3 rounded-2xl bg-red-50 text-red-600 font-semibold hover:bg-red-100 transition flex items-center justify-center gap-2">
        <i className="fa-solid fa-arrow-right-from-bracket"></i> Cerrar sesión
      </button>
    </div>
  );
}
